import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { modeRegistry, getMode, ModeDefinition } from '../../data/modeRegistry';
interface ModeSelectorProps {
  selectedModeId: string;
  onSelect: (id: string) => void;
}
export function ModeSelector({
  selectedModeId,
  onSelect
}: ModeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedMode = getMode(selectedModeId);
  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Escape to close
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };
  return <div className="relative inline-block text-left" data-id="element-2447">
      {/* Pill Button */}
      <button ref={buttonRef} onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 text-xs font-medium text-slate-300 transition-all duration-200" data-id="element-2448">
        <span className="text-sm leading-none" data-id="element-2449">{selectedMode.icon}</span>
        <span data-id="element-2450">{selectedMode.name}</span>
        <ChevronDown size={12} className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2451" />
      </button>

      {/* Dropdown (opens upwards) */}
      {isOpen && <div ref={dropdownRef} className="absolute bottom-full left-0 mb-2 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-2452">
          <div className="py-1" data-id="element-2453">
            {modeRegistry.map(mode => <button key={mode.id} onClick={() => handleSelect(mode.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${selectedModeId === mode.id ? 'bg-slate-800/80' : 'hover:bg-slate-800/40'}`} data-id="element-2454">
                <span className="text-base w-5 text-center leading-none flex-shrink-0" data-id="element-2455">
                  {mode.icon}
                </span>
                <div className="flex-1 min-w-0" data-id="element-2456">
                  <div className="flex items-center justify-between" data-id="element-2457">
                    <span className={`text-sm font-medium ${selectedModeId === mode.id ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-2458">
                      {mode.name}
                    </span>
                    <div className="flex items-center gap-2" data-id="element-2459">
                      {mode.shortcut && <span className="text-[10px] text-slate-600 font-mono" data-id="element-2460">
                          {mode.shortcut}
                        </span>}
                      {selectedModeId === mode.id && <Check size={14} className="text-slate-400 flex-shrink-0" data-id="element-2461" />}
                    </div>
                  </div>
                </div>
              </button>)}
          </div>
        </div>}
    </div>;
}